import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnInit,
  Output, QueryList, ViewChild, ViewChildren
} from '@angular/core';
import { AppState } from '@root/app-state.module';
import { MemoizedSelector, select, Store } from '@ngrx/store';
import {
  selectAllRecordsSelected,
  selectAnnotations,
  selectAnnotationToolStatus,
  selectCheckedPropertyIds,
  selectCheckedPropertyIdsForMobile,
  selectDrawingLines,
  selectDrawingShapes,
  selectDrawingTools,
  selectDriveDirectionResponse,
  selectIsMapVisible,
  selectLocateOnMapToolState,
  selectLotDimensions,
  selectMapCoordinates,
  selectMapMessage,
  selectMapTypeId,
  selectMapZoom,
  selectSearchResult,
  selectSelectedPointsOfInterestProperty,
  selectSelectedRowId,
  selectSelectedSearch,
  selectSidePanelState,
  selectSidePanelTab
} from '@store/search-board/search-board.selector';

import {
  selectedMapLayer,
  selectFloodZoneMarker,
  selectPropertyInformation,
  selectSelectedBoundaries,
  selectSelectedBoundaryById,
  selectSelectedDistressedPropertiesLayers,
  selectSelectedLocationInformationLayers,
  selectSelectedMlsListingsLayers,
  selectSelectedPropertyCharacteristicLayers,
  selectSelectedSalesValuesLayers,
  selectSelectedTrendsLayers,
  selectTrendsLayerMarker,
  selectTrendsOpacity
} from '@store/map-layers.selector';
import {
  FloodZoneData,
  INDICATOR_CONSTS,
  LayerMarker,
  MapPropertyInfo,
  PointOfInterestPoint,
  PointOfInterestPoints,
  PREFERENCES_SAVE_DEBOUNCE_TIME,
  PropertyData,
  PropertyInformation,
  PropertyPhotosRequest,
  RecBounds,
  SelectedRow,
  Shape,
  ShapeCircleCenter,
  SidePanelTab,
  SidePanelView,
  TrendsLayerData
} from '@store/search-board/search-board.model';
import { BehaviorSubject, combineLatest, Observable, Observer, of } from 'rxjs';
import {
  debounceTime, delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  mergeMap,
  pairwise,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';
import {
  AddAnnotation,
  AddDrawingFigure,
  AddDrawingLine,
  ChangeAnnotationToolStatus,
  ChangeDrawingTool,
  ChangeMapTypeId,
  ChangeMapZoom,
  ChangeShape,
  DeleteAnnotation,
  GetFloodZoneData,
  GetInstanetLink,
  GetLotDimensions,
  GetMapLegendData,
  GetPropertyInformation,
  GetPropertyReverseLink,
  GetReverseLink,
  GetTrendsLayerData, OpenDisclaimer,
  OpenPictureViewer,
  RemoveDrawingFigure,
  RemoveDrawingLine,
  SelectRow,
  SendEmailDriveDirection,
  SetDriveDirection,
  SetDriveDirectionResult,
  SetMapMessage,
  SubmitSearch
} from '@store/search-board/search-board.actions';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  GmMarker,
  GmMarkerIcon,
  drawingManagerStyles, LatLong,
  LayerInfo,
  LotDimension,
  MAP_ICON_SIZE,
  RegionValue
} from '@root/search/models/map.models';
import { google as googleModel, MapTypes, OverlayType } from '@shared/models/googlemaps.model';
import { drawParcelBoundary, hideLotDimensions, showLotDimensions } from '@shared/utils/lot-dimensions';
import { createBoundariesWmsOverlay } from '@shared/utils/map-wms';
import { debounce } from '@shared/utils/debounce';
import { FormatterService } from '@shared/services/formatter.service';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { Clipboard } from '@angular/cdk/clipboard';
import {
  selectInstanetLinkAvailable,
  selectIsMobile,
  selectPreferenceElement,
  selectPreferenceValue,
  selectReverseLinkAvailable,
  selectShapeMaxArea
} from '@store/user/user.selector';
import {
  DRIVE_DIRECTION_ROUTE_STYLE,
  EMPTY_AGM_MARKER,
  IDENTIFY_PROPERTY_MAX_ZOOM,
  LayerId,
  MAX_ZOOM,
  MIN_ZOOM,
  POINT_OF_INTEREST_MIN_ZOOM,
  PointOfInterestProperty,
  PointOfInterestTypeToPlacesType,
  TREND_LAYERS
} from '@root/search/models/map.constants';
import { selectSavedProperties } from '@store/saved-properties/saved-properties.selector';
import { MapUtils } from '@shared/utils/map-utils';
import { PropertyUtil } from '@shared/utils/property-util';
import { SaveMapBoundsPreferencesAction } from '@store/user/user.actions';
import { NavigationService } from '@root/search/services/navigation.service';
import { KeyCodes } from '@shared/utils/constants';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { PreferenceCode, Template } from '@store/user/user.model';
import { SearchMixpanelUtil } from '@root/search/search-mixpanel-util';

declare const google: any;
import OverlayCompleteEvent = googleModel.maps.drawing.OverlayCompleteEvent;
import Map = googleModel.maps.Map;
import Circle = googleModel.maps.Circle;
import Rectangle = googleModel.maps.Rectangle;
import Polygon = googleModel.maps.Polygon;
import Polyline = googleModel.maps.Polyline;
import LatLngBounds = googleModel.maps.LatLngBounds;
import LatLng = googleModel.maps.LatLng;
import LatLngLiteral = googleModel.maps.LatLngLiteral;
import MouseEvent = googleModel.maps.MouseEvent;
import MapsEventListener = googleModel.maps.MapsEventListener;
import DirectionsResult = googleModel.maps.DirectionsResult;
import DirectionsLeg = googleModel.maps.DirectionsLeg;
import DirectionsRoute = googleModel.maps.DirectionsRoute;
import DirectionsStep = googleModel.maps.DirectionsStep;
import Marker = googleModel.maps.Marker;
import Size = googleModel.maps.Size;
import Padding = googleModel.maps.Padding;
import Autocomplete = googleModel.maps.places.Autocomplete;
import PlaceResult = googleModel.maps.places.PlaceResult;
import { GoogleMapLoader } from '@root/dashboard/components/google-map/services/google-map-loader';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { NativeDrawingManager, NativeDrawingOptions } from './native-drawing-manager';
import MapTypeStyle = google.maps.MapTypeStyle;

const ONE_SECOND = 1000;
const POI_DEBOUNCE = 1000;

@UntilDestroy()
@Component({
  selector: 'rlst-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapComponent implements OnInit {

  private sidePanelTabOpened: SidePanelTab;
  private readonly searchMixpanelUtil: SearchMixpanelUtil = new SearchMixpanelUtil(this.store$);
  savedProperties: PropertyInformation[];
  savedPropertyIdList: string[];

  public clickedCoordinates: LatLng;

  constructor(private readonly store$: Store<AppState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly elementRef: ElementRef,
    private readonly ribbonService: RibbonService,
    private readonly zone: NgZone,
    private readonly navigationService: NavigationService,
    private readonly formatterService: FormatterService,
    private readonly mixpanelService: MixpanelService,
    private readonly clipboard: Clipboard,
    public googleMapLoader: GoogleMapLoader) {
    this.store$
      .pipe(
        select(selectMapTypeId),
        untilDestroyed(this)
      )
      .subscribe((mapTypeId: MapTypes) => {
        this.currentMapTypeId = mapTypeId;
        if (this.map) {
          this.mapOptions = {
            ...this.mapOptions,
            mapTypeId,
            center: { lat: this.map?.getCenter().lat(), lng: this.map?.getCenter().lng() },
            zoom: this.map?.getZoom()
          }
        } else {
          this.mapOptions = {
            ...this.mapOptions,
            mapTypeId
          }
        }

        this.cdr.markForCheck();
      });

    this.store$
      .pipe(
        select(selectShapeMaxArea),
        untilDestroyed(this)
      )
      .subscribe((shapeArea: number) => this.SHAPE_MAX_AREA = shapeArea);
  }

  @Input() isMobile: boolean;
  @Input() checkedProperties: Array<PropertyInformation>;
  @Input() clearPOI: Observable<boolean>;
  @Output() mapIsLoaded: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(GoogleMap, { static: false }) public googleMap: GoogleMap;
  @ViewChild('shapeMarker') shapeMarker: MapMarker;
  @ViewChild('driveMarker') driveMarker: MapMarker;
  @ViewChild('propInfoMarker') propInfoMarker: MapMarker;
  @ViewChild('floodMarker') floodMarker: MapMarker;
  @ViewChild('trendMarker') trendMarker: MapMarker;
  @ViewChild('selectedMarkerRef') selectedMarkerRef: MapMarker;
  @ViewChildren('annotationMarker') annotationMarker: QueryList<MapMarker>;
  @ViewChildren(MapMarker) mapMarker: QueryList<MapMarker>;
  @ViewChildren(MapInfoWindow) infoWindows: QueryList<MapInfoWindow>;
  @ViewChildren('annotationInfoWindow') annotationInfoWindows: QueryList<MapInfoWindow>;
  @ViewChild('selectedMarkerInfoWindow') selectedMarkerInfoWindow: MapInfoWindow;
  @ViewChild('trendInfoWindow') trendInfoWindow: MapInfoWindow;
  @ViewChild('driveInfoWindow') driveInfoWindow: MapInfoWindow;
  @ViewChild('floodInfoWindow') floodInfoWindow: MapInfoWindow;
  @ViewChild('shapeInfoWindow') shapeInfoWindow: MapInfoWindow;
  @ViewChild('propInfoWindow') propInfoWindow: MapInfoWindow;
  @ViewChild('coordinatesInfoWindow') coordinatesInfoWindow: MapInfoWindow;

  public shapeName: string;
  public shapeArea: number = 0;
  public shapeRadius: number = 0;
  public shapeDistance: number = 0;
  public shapeLat: number;
  public shapeLong: number;
  public shapeId: number;
  public shapeIsOpen: boolean = false;
  public shapeType: string;

  public totalShapesArea: number;

  public readonly MIN_ZOOM: number = MIN_ZOOM;
  public readonly MAX_ZOOM: number = MAX_ZOOM;
  public map: Map;
  public styles: Array<MapTypeStyle> = [];
  public currentMapTypeId: MapTypes;
  public markers: Array<GmMarker>;
  public selectedMarker: GmMarker = null;
  public annotations$: Observable<Array<GmMarker>>;

  public driveDirectionWindowIsOpen: boolean = false;
  public driveDirectionWindowLat: number;
  public driveDirectionWindowLong: number;
  public driveDirectionLeg: DirectionsLeg;
  private driveDirectionListeners: Array<MapsEventListener> = [];
  private driveDirectionObjects: Array<Marker | Polyline> = [];
  private driveDirectionIsOpen: boolean = false;

  private statusToIconAndTitleMap: { [key: string]: Partial<GmMarker> };

  private visibleMarkerBounds: LatLngBounds;

  private drawingManager: NativeDrawingManager;
  // True while a drawing tool is active. Used to disable marker clicks so vertex
  // clicks reach the map instead of opening property popups / panning the map.
  public isDrawingActive: boolean = false;
  private idCounter: number = 1;
  private annotationLength: number;

  private autoCompleteManager: Autocomplete;
  public locateOnMapMarker: GmMarker = null;

  private readonly mapSubject: BehaviorSubject<Map> = new BehaviorSubject<Map>(null);
  private mapLoaded$: Observable<Map>;
  private mapClicks$: Observable<MouseEvent>;
  private mapBounds$: Observable<LatLngBounds>;
  public propertyInfoMarker: GmMarker = EMPTY_AGM_MARKER;

  private sidePanelState$: Observable<SidePanelView>;

  private readonly MILES_IN_METER: number = 0.000621371;
  private readonly FEET_IN_METER: number = 3.28084;
  private readonly SQUARE_MILES_IN_SQUARE_METER: number = 3.86102e-7;
  private shapes: Array<Shape> = [];
  private shapeOverlays: Array<OverlayCompleteEvent> = [];
  private measurementToolOverlays: Array<OverlayCompleteEvent> = [];

  public isInstanetLinkAvailable$: Observable<boolean>;
  public isReverseLinkAvailable$: Observable<boolean>;
  private locateOnMapState$: Observable<boolean>;
  private checkedPropertyIds$: Observable<Array<string>>;

  public allowPointsOfInterest$: Observable<boolean>;
  public poiLayer: Array<PointOfInterestPoints> = [];
  public fetchPointsOfInterest$: Observable<Array<PointOfInterestPoints>>;
  public poiUpdateInProgress: number = 0;
  public previousPoiCategories: Array<PointOfInterestProperty> = [];
  public trendsLayerMarker$: Observable<LayerMarker<TrendsLayerData>>;
  public floodZoneMarker$: Observable<LayerMarker<FloodZoneData>>;
  public mapMessage$: Observable<string>;
  public searchResults$: Observable<Array<PropertyInformation>>;

  public SHAPE_MAX_AREA: number;
  public isRotateControlHidden: boolean = true;
  private isCtrlKeyPressed: boolean = false;
  private readonly RIBBON_SHOW_TIME: number = 5000;
  private readonly LOCATE_ON_MAP_ZOOM_LVL: number = 17;
  private readonly CIRCLE_EVENTS: Array<string> = ['click', 'radius_changed', 'center_changed', 'bounds_changed'];
  private readonly RECTANGLE_EVENTS: Array<string> = ['click', 'bounds_changed'];
  private readonly PATH_EVENTS: Array<string> = ['click', 'insert_at', 'set_at', 'move_at', 'dragend', 'remove_at', 'mouseup'];
  private readonly shapeEventsCallback = (shapeObj: OverlayCompleteEvent, eventName: string) => () => {
    this.zone.run(() => {
      this.recalculateInfoWindow(shapeObj, eventName);
    });
  };
  private suppressHideParcelBoundary: boolean = false;
  public mapOptions: google.maps.MapOptions = {
    disableDoubleClickZoom: true,
    disableDefaultUI: true,
    zoomControl: false,
    streetViewControl: true,
    scaleControl: true,
    minZoom: this.MIN_ZOOM,
    maxZoom: this.MAX_ZOOM,
  }

  @HostListener('document:keydown', ['$event'])
  setCtrlKey(event: KeyboardEvent) {
    const key = event.keyCode;
    if (key === KeyCodes.CONTROL || key === KeyCodes.COMMAND) {
      this.isCtrlKeyPressed = true;
    }
  }

  @HostListener('document:keyup', ['$event'])
  unsetCtrlKey(event: KeyboardEvent) {
    const key = event.keyCode;
    if (key === KeyCodes.CONTROL || key === KeyCodes.COMMAND) {
      this.isCtrlKeyPressed = false;
    }
  }

  public mapRightClick(mouseEvent: MouseEvent): void {
    this.clickedCoordinates = mouseEvent.latLng;
    this.coordinatesInfoWindow?.close();
    this.coordinatesInfoWindow?.open();
    this.coordinatesInfoWindow?.infoWindow.set('headerDisabled', false);
  }

  public copyToClipboard(): void {
    this.clipboard.copy(`${this.clickedCoordinates.lat()}, ${this.clickedCoordinates.lng()}`);
    this.coordinatesInfoWindow?.close();
    this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Copied to Clipboard');
  }

  ngOnInit(): void {
    this.sidePanelState$ = this.store$.pipe(
      select(selectSidePanelState),
      distinctUntilChanged((a: SidePanelView, b: SidePanelView) => a.sidePanelTabOpened === b.sidePanelTabOpened),
    );
    this.isInstanetLinkAvailable$ = this.store$.pipe(select(selectInstanetLinkAvailable));
    this.floodZoneMarker$ = this.store$.pipe(select(selectFloodZoneMarker), tap((layerMarker) => {
      if (layerMarker.isOpen) {
        setTimeout(() => {
          this.openInfoWindow(this.floodMarker, 'floodMarker');
          this.moveToMarkerInfoWindow(layerMarker);
        }, 300)
      }
    }));
    this.trendsLayerMarker$ = this.store$.pipe(
      select(selectTrendsLayerMarker),
      filter((trendsLayerMarker: LayerMarker<TrendsLayerData>) => {
        return trendsLayerMarker && !!trendsLayerMarker.data;
      }),
      map((trendsLayerMarker: LayerMarker<TrendsLayerData>) => {
        const data = trendsLayerMarker.data.map((trendsLayerData: TrendsLayerData) => {
          return {
            ...trendsLayerData,
            regionValue: this.formatterService.formatRegionValue(trendsLayerData.regionValue, trendsLayerMarker.layerId)
          };
        });

        const trendLayer = TREND_LAYERS.find((layer: LayerInfo) => layer.layerId === trendsLayerMarker.layerId);

        return {
          ...trendsLayerMarker,
          layerLabel: trendLayer && trendLayer.label,
          data
        };
      })
    );

    this.isReverseLinkAvailable$ = this.store$.pipe(select(selectReverseLinkAvailable));
    this.locateOnMapState$ = this.store$.pipe(select(selectLocateOnMapToolState));
    this.mapMessage$ = this.store$.pipe(select(selectMapMessage));
    this.checkedPropertyIds$ = this.store$.pipe(
      select(selectIsMobile),
      switchMap((isMobile: boolean) => this.store$
        .pipe(select(isMobile ? selectCheckedPropertyIdsForMobile : selectCheckedPropertyIds))));
    this.searchResults$ = this.store$.pipe(
      select(selectSearchResult),
      map((props: Array<PropertyInformation>) =>
        props && props
          .filter((p: PropertyInformation) => !!p.propertyIdentifier.latitude || !!p.propertyIdentifier.longitude) || [])
    );

    combineLatest([
      this.store$.pipe(
        select(selectSelectedTrendsLayers),
        map((layers) => layers.length > 0 ? 'Use CTRL + Click to see shape value' : ''),
        distinctUntilChanged()
      ),
      this.store$.pipe(
        select(selectSelectedBoundaryById, LayerId.FLOOD_ZONE_LAYER),
        map((layer) => layer ? 'Use CTRL + Click to see flood data' : ''),
        distinctUntilChanged()
      )
    ])
      .pipe(
        debounceTime(ONE_SECOND),
        untilDestroyed(this)
      )
      .subscribe(([trendsMapMessage, floodZoneMapMessage]: [string, string]) => {
        this.store$.dispatch(new SetMapMessage(trendsMapMessage || floodZoneMapMessage));
      });

    this.mapLoaded$ = this.mapSubject.asObservable().pipe(
      filter((mapObj) => !!mapObj)
    );

    combineLatest([this.mapLoaded$, this.searchResults$])
      .pipe(
        tap(([mapObj, searchResults]) => {
          this.markers = mapObj ? this.createMarkers(searchResults) : [];
          if (!this.map) {
            return;
          }
          if (this.markers.length === 1) {
            this.map.panTo(this.markers[0]);
          }
        }),
        switchMap(() => combineLatest([this.store$.pipe(select(selectSelectedRowId)), this.checkedPropertyIds$])),
        untilDestroyed(this)
      ).subscribe(([selectedRowId, checkedPropertyIds]: [SelectedRow, Array<string>]) => {

        this.markers.forEach((marker: GmMarker) =>
          marker.visible = checkedPropertyIds.includes(PropertyUtil.getIdFromPropertyData(marker.propertyData)));

        let selectedIndex: number | null;
        selectedIndex = this.markers.findIndex((marker) =>
          PropertyUtil.getIdFromPropertyData(marker.propertyData) === selectedRowId.current
        );
        const selectedMarker = selectedIndex >= 0 ? this.markers[selectedIndex] : null;
        if (selectedMarker) {
          selectedMarker.isOpen = selectedMarker.visible;

          if (selectedMarker.isOpen && selectedIndex !== null) {
            this.selectedMarker = selectedMarker;
            this.closeInfoWindows();
            this.cdr.detectChanges();
            setTimeout(() => {
              if (this.selectedMarkerRef && this.selectedMarkerInfoWindow) {
                this.selectedMarkerInfoWindow.open(this.selectedMarkerRef, false);
                this.selectedMarkerInfoWindow.infoWindow.set('headerDisabled', false);
              }
            }, 300);
          }
          this.hideLotDimensions();
        }
        const deselectedMarker = this.markers.find((marker) =>
          PropertyUtil.getIdFromPropertyData(marker.propertyData) === selectedRowId.previous);
        if (deselectedMarker) {
          deselectedMarker.isOpen = false;
          if (this.selectedMarker && PropertyUtil.getIdFromPropertyData(this.selectedMarker.propertyData) === selectedRowId.previous) {
            this.selectedMarker = null;
          }
        }
        this.cdr.detectChanges();
      });

    this.store$.pipe(
      select(selectDrawingShapes),
      untilDestroyed(this)
    ).subscribe((shapes: Array<Shape>) => {
      this.shapes = shapes;
      this.shapeOverlays = this.shapeOverlays
        .reduce((prev: Array<OverlayCompleteEvent>, curr: OverlayCompleteEvent) => {
          const removedShape = shapes.find((shape: Shape) => curr.overlay.get('myID') === shape.id);
          if (!removedShape) {
            this.shapeIsOpen = false;
            this.clearShape(curr);
            this.cdr.detectChanges();

            return prev;
          }
          prev.push(curr);
          this.cdr.detectChanges();
          return prev;
        }, []);
    });

    this.store$.pipe(
      select(selectDrawingLines),
      untilDestroyed(this)
    ).subscribe((shapes: Array<Shape>) => {
      this.measurementToolOverlays = this.measurementToolOverlays
        .reduce((prev: Array<OverlayCompleteEvent>, curr: OverlayCompleteEvent) => {
          const removedShape = shapes.find((shape: Shape) => curr.overlay.get('myID') === shape.id);
          if (!removedShape) {
            this.shapeIsOpen = false;
            this.clearShape(curr);
            this.cdr.detectChanges();

            return prev;
          }
          prev.push(curr);
          return prev;
        }, []);
    });

    this.annotations$ = this.store$
      .pipe(
        select(selectAnnotations),
        tap((annotations: Array<GmMarker>) => this.annotationLength = annotations.length),
      );

    this.locateOnMapState$
      .pipe(
        first(Boolean),
        untilDestroyed(this)
      )
      .subscribe(() => this.locateOnMapHandler());

    this.fetchPointsOfInterest$ =
      this.store$
        .pipe(
          select(selectSelectedPointsOfInterestProperty),
          mergeMap((poiCategories: Array<PointOfInterestProperty>) => {
            const added = poiCategories.filter((v) => this.previousPoiCategories.indexOf(v) < 0);
            const removed = this.previousPoiCategories.filter((v) => poiCategories.indexOf(v) < 0);

            // POI Category was checked from the POI dropdown. Only fetch the category that was checked. When the page is loaded,
            // this could be a list of added categories.
            if (added.length > 0) {
              added.forEach((categoryName) => {
                this.poiUpdateInProgress++;
                this.fetchPOI(categoryName).subscribe(() => {
                  this.poiUpdateInProgress--;
                });
              });
            }

            // POI Category was unchecked from the POI dropdown. Only delete the category that was checked. At most 1 at a time.
            if (removed.length > 0) {
              this.poiLayer = this.poiLayer.filter((categories) => !categories.hasOwnProperty(removed[0]));
            } else if (added.length === 0 && removed.length === 0) {
              // Map was moved or zoomed. Refresh everything.
              if (this.poiUpdateInProgress) {
                return of(this.poiLayer);
              }

              poiCategories.forEach((categoryName) => {
                this.poiUpdateInProgress++;
                this.fetchPOI(categoryName).subscribe(() => {
                  this.poiUpdateInProgress--;
                });
              });
            }
            this.previousPoiCategories = poiCategories;
            return of(this.poiLayer);
          }),
          untilDestroyed(this)
        );

    this.clearPOI.pipe(
      untilDestroyed(this)
    ).subscribe((clearButtonPushed: boolean) => {
      if (clearButtonPushed) {
        this.poiLayer = [];
      }
    });

    this.store$
      .pipe(
        select(selectSidePanelState),
        untilDestroyed(this)
      )
      .subscribe((sidePanelView: SidePanelView) => {
        this.sidePanelTabOpened = sidePanelView.sidePanelTabOpened;
      });

    this.store$.pipe(
      select(selectSavedProperties),
      untilDestroyed(this)
    ).subscribe((savedProperties: Array<PropertyInformation>) => {
      this.savedProperties = savedProperties;
      this.savedPropertyIdList = savedProperties
        .map((property: PropertyInformation) => PropertyUtil.getCombinedIdFromPropertyInformation(property));
      this.cdr.markForCheck();
    });

    this.googleMapLoader.isApiLoaded
      .pipe(
        untilDestroyed(this),
        filter(Boolean),
        delay(1),
        switchMap(() => this.googleMap.idle),
        take(1),
        tap(() => this.mapReady(this.googleMap.googleMap as unknown as Map)),
        switchMap(() => this.googleMap.zoomChanged))
      .subscribe(() => {
        this.zoomChange(this.googleMap.getZoom());
      })
  }

  public mapReady(event: Map): void {
    this.map = event;

    this.store$.pipe(
      select(selectMapCoordinates),
      untilDestroyed(this),
      filter((coordinates: Array<LatLngLiteral>) => coordinates && coordinates.length > 1)
    ).subscribe((coordinates: Array<LatLngLiteral>) => {
      const bounds = this.adjustBounds(coordinates[0], coordinates[1]);
      this.map.fitBounds(new google.maps.LatLngBounds(bounds[0], bounds[1]), 0);
    });

    this.store$.pipe(
      select(selectDriveDirectionResponse),
      untilDestroyed(this),
      filter(Boolean)
    ).subscribe((response: DirectionsResult) => this.onDriveDirectionResponse(response));

    this.initDrawingManager(event);

    this.statusToIconAndTitleMap = {
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ACTIVE]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/active.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/active_hover.png', MAP_ICON_SIZE),
        title: 'Active'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_EXPIRED]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/expired.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/expired_hover.png', MAP_ICON_SIZE),
        title: 'Expired'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_PENDING]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/pending.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/pending_hover.png', MAP_ICON_SIZE),
        title: 'Pending Sale'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_SOLD]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/sold.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/sold_hover.png', MAP_ICON_SIZE),
        title: 'Sold/Closed Sale'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_DEFAULT]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/default.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/default_hover.png', MAP_ICON_SIZE),
        title: 'No MLS Activity'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_SHOW]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/active.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/active_hover.png', MAP_ICON_SIZE),
        title: 'Active Under Contract'
      },
      [INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_NOT_SHOW]: {
        icon: MapUtils.createIconForMarker('assets/img/agm/pending.png', MAP_ICON_SIZE),
        selectedIcon: MapUtils.createIconForMarker('assets/img/agm/pending_hover.png', MAP_ICON_SIZE),
        title: 'Pending'
      }
    };

    this.mapSubject.next(event);
    this.mapIsLoaded.emit();
    // this.mapContainer = this.elementRef.nativeElement.querySelector('.agm-map-container-inner');

    this.store$.pipe(
      select(selectMapZoom),
      take(1),
    ).subscribe((zoom: number) =>
      this.map.setZoom(zoom));

    this.getWmsOverlay(this.store$.pipe(select(selectSelectedTrendsLayers)))
      .pipe(
        tap((wmsOverlay) => this.map.overlayMapTypes.setAt(0, wmsOverlay)),
        filter<googleModel.maps.ImageMapType>(Boolean),
        switchMap((wmsOverlay) => {
          return this.store$.pipe(select(selectTrendsOpacity))
            .pipe(
              tap((opacityLevel) => {
                wmsOverlay.setOpacity(opacityLevel / 100);
              })
            );
        }),
        untilDestroyed(this)
      )
      .subscribe();

    combineLatest([
      this.store$.pipe(select(selectSidePanelTab)),
      this.store$.pipe(select(selectPreferenceValue(PreferenceCode.PG_RL_PREMIUM_SEARCH, PreferenceCode.PREF_RL_PREMIUM_SEARCH))),
      this.store$.pipe(select(selectPreferenceElement(PreferenceCode.PG_MAP_TOOLS, PreferenceCode.PREF_PREMIUM_MAP_LAYERS))),
    ]).pipe(
      filter(([currentSidePanel, hasSearch, layerPreference]) => {
        this.map.overlayMapTypes.setAt(1, null);
        return currentSidePanel === SidePanelTab.mySearch && hasSearch !== 'N' && layerPreference.hasAccess;
      }),
      switchMap(() => {
        return this.getWmsOverlay(this.store$.pipe(select(selectedMapLayer)), 1);
      }),
      tap((wmsOverlay) => {
        this.map.overlayMapTypes.setAt(1, wmsOverlay);
      }),
      switchMap((wmsOverlay) => {
        return this.store$.pipe(select(selectTrendsOpacity))
          .pipe(
            tap((opacityLevel) => {
              if (wmsOverlay) {
                wmsOverlay.setOpacity(opacityLevel / 100);
              }
            })
          );
      }),
      untilDestroyed(this)
    ).subscribe();

    [
      selectSelectedBoundaries,
      selectSelectedDistressedPropertiesLayers,
      selectSelectedSalesValuesLayers,
      selectSelectedMlsListingsLayers,
      selectSelectedLocationInformationLayers,
      selectSelectedPropertyCharacteristicLayers
    ].forEach((selector: MemoizedSelector<AppState, Array<LayerInfo>>, index: number) => {
      this.drawWmsOverlay(this.store$.pipe(select(selector)), index + 2);
      // +1 for trends layer that is added separately +1 for tools layer that is added separately
    });

    this.store$.pipe(
      select(selectLotDimensions),
      filter((lotDimensions: Array<LotDimension>) => lotDimensions && !!lotDimensions.length),
      untilDestroyed(this)
    ).subscribe((lotDimensions: Array<LotDimension>) => {
      showLotDimensions(lotDimensions, this.map);
    });

    this.store$.pipe(
      select(selectPropertyInformation),
      tap((propertyInformation: MapPropertyInfo) => {
        if (propertyInformation) {
          drawParcelBoundary(propertyInformation.parcelGoemetry, this.map);
        }
      }),
      map<MapPropertyInfo, GmMarker>((propertyInformation: MapPropertyInfo) => {
        if (propertyInformation) {
          return {
            lat: parseFloat(propertyInformation.latitude),
            lng: parseFloat(propertyInformation.longitude),
            draggable: false,
            isOpen: true,
            parcelId: propertyInformation.parcelId,
            parcelSeqNumber: parseInt(propertyInformation.parcelSeq, 10).toString(),
            fipsCode: propertyInformation.countyId,
            provisionedProperty: propertyInformation.provisionedProperty,
            taxId: propertyInformation.taxId,
            propertyData: MapUtils.mapPropertyInfoToPropertyData(propertyInformation)
          };
        }
        return EMPTY_AGM_MARKER;
      }),
      untilDestroyed(this)
    )
      .subscribe((propertyInfoMarker: GmMarker) => {
        this.propertyInfoMarker = propertyInfoMarker;
        if (this.propertyInfoMarker.isOpen) {
          setTimeout(() => {
            this.openInfoWindow(this.propInfoMarker, 'propInfoMarker')
            this.moveToMarkerInfoWindow(propertyInfoMarker);
          }, 300)
        }

        this.cdr.detectChanges();
      });

    this.trendsLayerMarker$
      .pipe(
        filter((trendsLayerMarker: LayerMarker<TrendsLayerData>) => trendsLayerMarker.isOpen),
        untilDestroyed(this)
      )
      .subscribe((trendsLayerMarker: LayerMarker<TrendsLayerData>) => {
        const geometry: Array<Array<string>> = trendsLayerMarker.data[0] && trendsLayerMarker.data[0].goemetery;
        drawParcelBoundary(geometry, this.map);

        setTimeout(() => {
          this.openInfoWindow(this.trendMarker, 'trendMarker');
          this.moveToMarkerInfoWindow(trendsLayerMarker);
        }, 300)

        this.cdr.detectChanges();
      });

    this.mapClicks$ = new Observable((observer: Observer<MouseEvent>) => {
      const clickListener = google.maps.event.addListener(this.map, 'click', (mouseEvent: MouseEvent) => {
        observer.next(mouseEvent);
      });

      return () => google.maps.event.removeListener(clickListener);
    });



    this.mapClicks$.pipe(
      withLatestFrom(
        this.store$.pipe(select(selectMapZoom)),
        this.store$.pipe(select(selectAnnotationToolStatus)),
        this.store$.pipe(select(selectSelectedTrendsLayers)),
        this.store$.pipe(select(selectSelectedBoundaryById, LayerId.FLOOD_ZONE_LAYER))
      ),
      untilDestroyed(this)
    )
      .subscribe(([mouseEvent, zoomLevel, isAnnotationActive, trendsLayers, floodMapLayer]) => {
        // While a shape drawing tool is active, map clicks are vertex placements;
        // don't trigger property lookup, annotations or layer popups.
        if (this.isDrawingActive || this.drawingManager?.getDrawingMode()) {
          return;
        }

        this.coordinatesInfoWindow?.close();
        if (this.selectedMarkerInfoWindow) {
          if ((this.selectedMarkerInfoWindow.infoWindow as any).isOpen) {
            this.selectedMarkerInfoWindow.close();
          }
        }

        this.propertyInfoMarker.isOpen = false;
        this.openShape(false);

        if (this.floodMarker) {
          this.floodInfoWindow?.close();
        }

        if (this.trendMarker) {
          this.trendInfoWindow?.close();
        }

        if (isAnnotationActive) {
          this.drawAnnotation(mouseEvent);
          return;
        }

        if (this.isCtrlKeyPressed && !!trendsLayers.length) {
          const trendsLayer: LayerInfo = trendsLayers[0];
          const layerStyle = MapUtils.formatTrendsLayerStyleName(trendsLayer.layerStyle);
          const layerId = trendsLayer.layerId;

          this.store$.dispatch(new GetTrendsLayerData({
            lattitude: mouseEvent.latLng.lat(),
            longitude: mouseEvent.latLng.lng(),
            trendsLayerEnabled: true,
            floodZoneEnabled: false,
            layerStyle,
            layerId
          }));
          return;
        }

        if (this.isCtrlKeyPressed && !!floodMapLayer) {
          const layerStyle = MapUtils.formatTrendsLayerStyleName(floodMapLayer.layerStyle);
          const layerId = floodMapLayer.layerId;

          this.store$.dispatch(new GetFloodZoneData({
            lattitude: mouseEvent.latLng.lat(),
            longitude: mouseEvent.latLng.lng(),
            trendsLayerEnabled: false,
            floodZoneEnabled: true,
            layerStyle,
            layerId
          }));
          return;
        }

        if (zoomLevel >= IDENTIFY_PROPERTY_MAX_ZOOM) {
          this.store$.dispatch(new GetPropertyInformation({
            latitude: mouseEvent.latLng.lat(),
            longitude: mouseEvent.latLng.lng()
          }));
          this.propertyInfoMarker.isOpen = false;
          this.cdr.detectChanges();
        }
      });

    this.mapBounds$ = new Observable((observer: Observer<LatLngBounds>) => {
      const boundsChangedListener = google.maps.event.addListener(this.map, 'bounds_changed', () => {
        observer.next(this.map.getBounds());
      });

      return () => google.maps.event.removeListener(boundsChangedListener);
    });

    combineLatest([
      this.mapBounds$,
      this.store$.pipe(select(selectMapZoom)),
      this.store$.pipe(select(selectSelectedPointsOfInterestProperty))
    ])
      .pipe(
        filter(([_, mapZoom]) => mapZoom >= POINT_OF_INTEREST_MIN_ZOOM),
        debounceTime(POI_DEBOUNCE),
        switchMap(() => this.fetchPointsOfInterest$),
        untilDestroyed(this)
      ).subscribe();

    this.allowPointsOfInterest$ = this.store$.pipe(
      select(selectMapZoom),
      map((zoomLvl: number) => zoomLvl >= POINT_OF_INTEREST_MIN_ZOOM)
    );

    combineLatest([
      this.store$.pipe(
        select(selectSelectedTrendsLayers)
      ),
      this.mapBounds$.pipe(
        debounceTime(ONE_SECOND)
      )
    ])
      .pipe(
        filter(([trendsLayers]: [Array<LayerInfo>, LatLngBounds]) => !!trendsLayers.length),
        untilDestroyed(this)
      )
      .subscribe(([trendsLayers, mapBounds]: [Array<LayerInfo>, LatLngBounds]) => {
        const layerStyleName = MapUtils.formatTrendsLayerStyleName(trendsLayers[0].layerStyle);

        const regionValue: RegionValue = {
          layerStyleName,
          minX: mapBounds.getSouthWest().lng(),
          minY: mapBounds.getSouthWest().lat(),
          maxX: mapBounds.getNorthEast().lng(),
          maxY: mapBounds.getNorthEast().lat()
        };
        this.store$.dispatch(new GetMapLegendData(regionValue));
      });

    setTimeout(() => {
      this.mapBounds$
        .pipe(
          debounceTime(PREFERENCES_SAVE_DEBOUNCE_TIME),
          untilDestroyed(this)
        )
        .subscribe((mapBounds: LatLngBounds) => {
          this.store$.dispatch(
            new SaveMapBoundsPreferencesAction([mapBounds.getSouthWest().toJSON(), mapBounds.getNorthEast().toJSON()])
          );
        }
        );
    }, PREFERENCES_SAVE_DEBOUNCE_TIME);

    const MAP_TRANSITION_TIME = 300; // as-split component transition duration in ms

    this.searchResults$
      .pipe(
        switchMap(() => combineLatest([
          this.checkedPropertyIds$.pipe(startWith<null, Array<string>>(null), pairwise()),
          this.store$.pipe(select(selectIsMapVisible)),
          this.store$.pipe(select(selectAllRecordsSelected))
        ])),
        debounceTime(MAP_TRANSITION_TIME),
        filter(([_, isMapVisible]) => isMapVisible),
        untilDestroyed(this)
      )
      .subscribe(([[prevResult, currResult], _, allSelected]) => {
        this.visibleMarkerBounds = new google.maps.LatLngBounds();
        let lastChecked: GmMarker;
        let lastCheckedPropertyId: string;

        if (!allSelected && prevResult !== null && prevResult.length < currResult.length) {
          lastCheckedPropertyId = (currResult as unknown as Array<string>).find((propertyId: string) => !prevResult.includes(propertyId));
        }

        this.markers.forEach((marker: GmMarker) => {
          const propertyId = PropertyUtil.getIdFromPropertyData(marker.propertyData);
          marker.visible = currResult.includes(propertyId);
          if (marker.visible) {
            this.visibleMarkerBounds.extend({ lat: marker.lat, lng: marker.lng });
          }
          if (lastCheckedPropertyId && propertyId === lastCheckedPropertyId) {
            lastChecked = marker;
          }
        });

        if (!this.visibleMarkerBounds.isEmpty()) {
          const zoomToBounds = async () => {
            await this.map.fitBounds(this.visibleMarkerBounds);
          };
          zoomToBounds().then(() => {
            if (lastChecked) {
              lastChecked.isOpen = true;
            }
          });
        }
      });
  }

  public submitSearch(): void {
    this.store$.dispatch(new SubmitSearch());
  }

  public useDrawTool(toolName: OverlayType | null): void {
    if (this.drawingManager) {
      this.drawingManager.setDrawingMode(toolName);
      this.isDrawingActive = !!toolName;
      this.cdr.detectChanges();
    }
  }

  public changeMapTypeId(type: MapTypes) {
    this.store$.dispatch(new ChangeMapTypeId(type));
  }

  public snazzyInfoWindowIsToggled(marker: GmMarker) {
    this.moveToMarkerInfoWindow(marker);
    this.store$.dispatch(new SelectRow({ selection: true, rowId: PropertyUtil.getIdFromPropertyData(marker.propertyData) }));
  }

  public moveToMarkerInfoWindow(marker: GmMarker, height: number = 256) {
    this.map.panTo(marker);

    const element: HTMLDataElement = this.elementRef.nativeElement.querySelector('.info-window');
    const elementHeight = element ? element.getBoundingClientRect().height : height;
    this.map.panBy(0, (elementHeight / 2) * (-1));
  }

  public closeSnazzyWindow(marker: GmMarker): void {
    marker.isOpen = false;
    this.closeInfoWindows();
  }

  public hideSnazzyWindow(marker: GmMarker): void {
    this.suppressHideParcelBoundary = true;
    marker.isOpen = false;
  }

  public onMarkerClick(marker: GmMarker): void {
    // Ignore marker clicks while drawing so the click becomes a shape vertex
    // instead of opening the property info popup.
    if (this.isDrawingActive || this.drawingManager?.getDrawingMode()) {
      return;
    }
    this.selectedMarker = marker;
    marker.isOpen = true;
    this.snazzyInfoWindowIsToggled(marker);
    this.cdr.detectChanges();
    setTimeout(() => {
      this.openInfoWindow(this.selectedMarkerRef, 'selectedMarker');
    }, 0);
  }

  public closeSelectedMarker(): void {
    if (this.selectedMarker) {
      this.selectedMarker.isOpen = false;
      this.selectedMarker = null;
    }
    this.closeInfoWindows();
    this.cdr.detectChanges();
  }

  public zoomChange(zoomLvl: number): void {
    this.store$.dispatch(new ChangeMapZoom(zoomLvl));

    const MIN_ZOOM_LEVEL_TO_DISPLAY_ROTATE_CONTROL = 20;
    this.isRotateControlHidden = zoomLvl < MIN_ZOOM_LEVEL_TO_DISPLAY_ROTATE_CONTROL ||
      (this.currentMapTypeId !== MapTypes.SATELLITE && this.currentMapTypeId !== MapTypes.HYBRID);

    if (this.selectedMarker) {
      this.closeSelectedMarker();
    }
  }

  public getLotDimensions(marker: GmMarker): void {
    this.closeSnazzyWindow(marker);
    this.cdr.detectChanges();
    this.store$.dispatch(
      new GetLotDimensions({
        latLong: {
          latitude: marker.lat,
          longitude: marker.lng
        },
        parcelId: marker.parcelId,
        parcelSeq: marker.parcelSeqNumber,
        countyId: marker.fipsCode
      })
    );
  }

  public getInstanetLink(marker: GmMarker): void {
    this.store$.dispatch(new GetInstanetLink(marker.propertyData));
  }

  public getReverseLink(marker: GmMarker): void {
    this.store$.dispatch(new GetReverseLink(PropertyUtil.getIdFromPropertyData(marker.propertyData)));
  }

  public getPropertyReverseLink(marker: GmMarker): void {
    this.store$.dispatch(new GetPropertyReverseLink(marker.propertyData));
  }

  public hideLotDimensions(): void {
    if (!this.suppressHideParcelBoundary) {
      hideLotDimensions();
    }
    this.suppressHideParcelBoundary = false;
  }

  public createMarkers(searchResults: Array<PropertyInformation>): Array<GmMarker> {
    return searchResults.map((property: PropertyInformation) => {
      const { latitude, longitude, parcelId, parcelSeqNumber, fipsCode, apn } = property.propertyIdentifier;
      const { icon, title, selectedIcon } = this.statusToIconAndTitleMap[property.propertyData.MLS_STD_MLS_STATUS]
        || this.statusToIconAndTitleMap[INDICATOR_CONSTS.MLS_STD_MLS_STATUS_DEFAULT];
      return {
        lat: latitude,
        lng: longitude,
        draggable: false,
        isOpen: false,
        visible: false,
        icon,
        selectedIcon,
        title,
        parcelId,
        parcelSeqNumber,
        fipsCode,
        propertyData: property.propertyData,
        taxId: apn
      };
    });
  }

  public trackByMarkers(_: number, marker: GmMarker): string {
    return PropertyUtil.getIdFromPropertyData(marker.propertyData);
  }

  public zoomToProperty(marker: GmMarker, zoomLevel: number = MAX_ZOOM): void {
    this.map.setZoom(zoomLevel);
    this.map.panTo(marker);
  }

  public openInfoWindow(marker: MapMarker, markerType: string, index?: number) {
    this.closeInfoWindows();
    if (index !== undefined) {
      const infoWindowArray = this.infoWindows.toArray();
      if (index < infoWindowArray.length) {
        infoWindowArray[index].open(marker, false);
        infoWindowArray[index].infoWindow.set('headerDisabled', false);
      }
    } else {
      switch (markerType) {
        case 'selectedMarker':
          this.selectedMarkerInfoWindow?.close();
          this.selectedMarkerInfoWindow?.open(marker, false);
          this.selectedMarkerInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'floodMarker':
          this.floodInfoWindow?.close();
          this.floodInfoWindow?.open(marker, false);
          this.floodInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'trendMarker':
          this.trendInfoWindow?.close();
          this.trendInfoWindow?.open(marker, false);
          this.trendInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'driveMarker':
          this.driveInfoWindow?.close();
          this.driveInfoWindow?.open(marker, false);
          this.driveInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'shapeMarker':
          this.shapeInfoWindow?.close();
          this.shapeInfoWindow?.open(marker, false);
          this.shapeInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'propInfoMarker':
          this.propInfoWindow?.close();
          this.propInfoWindow?.open(marker, false);
          this.propInfoWindow?.infoWindow.set('headerDisabled', false);
          break;
        case 'annotationMarker':
          const infoWindowArray = this.annotationInfoWindows.toArray();
          infoWindowArray.forEach((infoWindow: MapInfoWindow, i: number) => {
            infoWindow.open(this.annotationMarker.get(i), false);
            infoWindow.infoWindow.set('headerDisabled', false);
          });
          break;
      }
    }
  }

  private initDrawingManager(mapObj: Map): void {
    const options: NativeDrawingOptions = {
      polygonOptions: { ...drawingManagerStyles },
      circleOptions: { ...drawingManagerStyles },
      rectangleOptions: { ...drawingManagerStyles },
      polylineOptions: { ...drawingManagerStyles }
    };

    // google.maps.drawing.DrawingManager was decommissioned in Maps JS v3.65 (May 2026).
    // NativeDrawingManager reproduces the interactive drawing behaviour with native overlays
    // and emits the same overlaycomplete-style event consumed by onOverlayComplete().
    this.drawingManager = new NativeDrawingManager(
      mapObj,
      options,
      (e: OverlayCompleteEvent) => this.onOverlayComplete(e)
    );

    this.store$
      .pipe(
        select(selectDrawingTools),
        untilDestroyed(this)
      )
      .subscribe((toolName: OverlayType | null) => this.useDrawTool(toolName));

    this.drawShapesFromSavedSearch(mapObj);
  }

  private onOverlayComplete(e: OverlayCompleteEvent): void {
    // Native map events fire outside Angular's zone; run inside it so store dispatch and
    // change detection behave exactly as they did under the old DrawingManager listener.
    this.zone.run(() => {
      this.overlayCompleteHandler(e);
      // Drawing finished: re-enable marker clicks.
      this.isDrawingActive = false;

      if (e.type !== OverlayType.POLYLINE) {
        this.logMapDrawingMixPanel('Search - Drawing Added', e.type);
        this.shapeOverlays.push(e);
        const totalShapesArea = this.getTotalShapesArea();
        this.showMessageIfTotalShapesAreaExceeded(totalShapesArea);
      } else {
        this.logMapDrawingMixPanel('Search - Measurement  Added', 'measurement');
        this.measurementToolOverlays.push(e);
      }
    });
  }

  private drawShapesFromSavedSearch(mapObj: Map): void {
    this.store$.pipe(
      select(selectSelectedSearch),
      withLatestFrom(this.store$.pipe(select(selectDrawingShapes))),
      switchMap(([_, shapes]: [Template, Array<Shape>]) =>
        this.sidePanelState$.pipe(map((sidePanelView: SidePanelView) => ([shapes, sidePanelView])))),
      untilDestroyed(this)
    ).subscribe(([shapes, sidePanelView]: [Array<Shape>, SidePanelView]) => {
      if (!shapes.length) {
        return;
      }

      if (this.shapeOverlays.length) {
        this.shapeOverlays.forEach((s: OverlayCompleteEvent) => {
          this.clearShape(s);
        });

        this.shapeOverlays = [];
      }

      if (sidePanelView.sidePanelTabOpened !== SidePanelTab.mySearch) {
        return;
      }

      shapes.forEach((s: Shape) => {
        let overlay;
        if (s.type === OverlayType.CIRCLE) {
          overlay = new google.maps.Circle({
            center: {
              lng: s.center.lng,
              lat: s.center.lat,
            },
            radius: s.radius,
            ...drawingManagerStyles
          });
        }
        if (s.type === OverlayType.POLYGON) {
          overlay = new google.maps.Polygon({
            paths: s.latLong,
            ...drawingManagerStyles
          });
        }
        if (s.type === OverlayType.RECTANGLE) {
          const bounds: RecBounds = s.bounds ? s.bounds : ({
            north: s.latLong[2].lat,
            south: s.latLong[1].lat,
            east: s.latLong[0].lng,
            west: s.latLong[2].lng
          });

          overlay = new google.maps.Rectangle({
            ...drawingManagerStyles,
            bounds
          });
        }

        overlay.set('myID', s.id);

        if (overlay.get('name') === undefined) {
          overlay.set('name', s.name);
        }

        this.shapeOverlays.push({ type: s.type, overlay });
        this.overlayCompleteHandler({ type: s.type, overlay }, false);
        overlay.setMap(mapObj);
      });
    });
  }

  private drawAnnotation(event: MouseEvent): void {
    const annotation = {
      lng: event.latLng.lng(),
      lat: event.latLng.lat(),
      parcelId: (Date.now() + this.idCounter).toString()
    };
    this.store$.dispatch(new ChangeAnnotationToolStatus());
    this.store$.dispatch(new AddAnnotation(annotation));

    setTimeout(() => {
      this.openInfoWindow(this.annotationMarker.get(this.annotationLength - 1), 'annotationMarker');
    }, 300)

    this.cdr.detectChanges();

    this.idCounter++;

    this.logMapLocateAnnotationMixPanel('Search - Annotation Added');
  }

  private overlayCompleteHandler(overlayComplete: OverlayCompleteEvent, needAddToStore: boolean = true): void {
    if (overlayComplete.overlay.get('myID') === undefined) {
      overlayComplete.overlay.set('myID', Date.now() + this.idCounter);
      this.idCounter = ++this.idCounter;
    }
    this.store$.dispatch(new ChangeDrawingTool(null));

    if (needAddToStore) {
      const shape = this.getShapeObject(overlayComplete);
      const action = (overlayComplete.type === OverlayType.POLYLINE)
        ? new AddDrawingLine(shape)
        : new AddDrawingFigure(shape);

      this.store$.dispatch(action);
    }

    this.attachListeners(overlayComplete);
  }

  private getShapeObject(overlayComplete: OverlayCompleteEvent): Shape {
    const id: number = overlayComplete.overlay.get('myID');
    const type: OverlayType = overlayComplete.type;
    const typeString = overlayComplete.type.toString();
    const name: string = typeString.charAt(0).toUpperCase() + typeString.slice(1) + (this.idCounter - 1);
    if (!overlayComplete.overlay.hasOwnProperty('name')) {
      overlayComplete.overlay.set('name', name);
    }

    return { id, name, type, ...this.getShapePositionData(overlayComplete) };
  }

  private getShapePositionData(overlayComplete: OverlayCompleteEvent): Partial<Shape> {
    const type: OverlayType = overlayComplete.type;
    let radius: number = null,
      latLong: Array<LatLngLiteral> = null,
      center: ShapeCircleCenter = null,
      bounds: RecBounds = null;

    if (type === OverlayType.CIRCLE) {
      const circle = overlayComplete.overlay as Circle;
      radius = circle.getRadius();
      center = { lat: circle.getCenter().lat(), lng: circle.getCenter().lng() };
    }

    if (type === OverlayType.POLYGON) {
      const polygon = overlayComplete.overlay as Polygon;
      latLong = polygon.getPath().getArray().map((el: LatLng) => ({ lat: el.lat(), lng: el.lng() }));
    }

    if (type === OverlayType.POLYLINE) {
      const polyline = overlayComplete.overlay as Polyline;
      latLong = polyline.getPath().getArray().map((el: LatLng) => ({ lat: el.lat(), lng: el.lng() }));
    }

    if (type === OverlayType.RECTANGLE) {
      const recBounds = (overlayComplete.overlay as Rectangle).getBounds();
      const NE = recBounds.getNorthEast();
      const SW = recBounds.getSouthWest();
      bounds = {
        north: NE.lat(),
        south: SW.lat(),
        east: NE.lng(),
        west: SW.lng()
      };
    }

    return { latLong, radius, center, bounds };
  }

  private attachListeners(shapeObj: OverlayCompleteEvent): void {
    if (shapeObj.type === OverlayType.CIRCLE) {
      this.CIRCLE_EVENTS.forEach((eventName: string) => {
        google.maps.event.addListener(shapeObj.overlay, eventName, this.shapeEventsCallback(shapeObj, eventName));
      });
    } else if (shapeObj.type === OverlayType.RECTANGLE) {
      this.RECTANGLE_EVENTS.forEach((eventName: string) => {
        google.maps.event.addListener(shapeObj.overlay, eventName, this.shapeEventsCallback(shapeObj, eventName));
      });
    } else if (shapeObj.type === OverlayType.POLYGON || shapeObj.type === OverlayType.POLYLINE) {
      this.PATH_EVENTS.forEach((eventName: string) => {
        google.maps.event.addListener(shapeObj.overlay, eventName, this.shapeEventsCallback(shapeObj, eventName));
      });
    }
  }

  private recalculateInfoWindow(shapeObj: OverlayCompleteEvent, eventName: string): void {
    const { lat, lng } = this.getPointToDisplayInfoWindow(shapeObj);
    this.shapeName = shapeObj.overlay.get('name');
    this.shapeId = shapeObj.overlay.get('myID');
    this.shapeLat = lat;
    this.shapeLong = lng;

    if (shapeObj.type !== OverlayType.POLYLINE) {
      const { radius, area } = this.computeAreaAndRadius(shapeObj);
      this.shapeRadius = radius;
      this.shapeArea = area;
      this.shapeDistance = 0;
      this.totalShapesArea = this.getTotalShapesArea();

      this.showMessageIfTotalShapesAreaExceeded(this.totalShapesArea);

      const shapeLatLngAndRadius = this.getShapePositionData(shapeObj);
      const changedShape = this.shapes.find((s: Shape) => s.id === shapeObj.overlay.get('myID'));
      let dispatchChangeShape: boolean;

      switch (changedShape.type) {
        case OverlayType.CIRCLE:
          dispatchChangeShape =
            shapeLatLngAndRadius.radius !== changedShape.radius ||
            shapeLatLngAndRadius.center.lat !== changedShape.center.lat ||
            shapeLatLngAndRadius.center.lng !== changedShape.center.lng;
          break;
        case OverlayType.RECTANGLE:
          if (changedShape.bounds) {
            dispatchChangeShape =
              shapeLatLngAndRadius.bounds.east !== changedShape.bounds.east ||
              shapeLatLngAndRadius.bounds.west !== changedShape.bounds.west ||
              shapeLatLngAndRadius.bounds.north !== changedShape.bounds.north ||
              shapeLatLngAndRadius.bounds.south !== changedShape.bounds.south;
          } else {
            dispatchChangeShape = true;
          }
          break;
        case OverlayType.POLYGON:
          dispatchChangeShape = shapeLatLngAndRadius.latLong.length !== changedShape.latLong.length ||
            shapeLatLngAndRadius.latLong.some((value: LatLngLiteral, i: number) =>
              value.lng !== changedShape.latLong[i].lng || value.lat !== changedShape.latLong[i].lat);
          break;
        default:
          dispatchChangeShape = false;
      }

      if (dispatchChangeShape) {
        this.store$.dispatch(new ChangeShape({
          id: this.shapeId,
          name: this.shapeName,
          ...shapeLatLngAndRadius
        }));
      }
    } else {
      this.shapeDistance = google.maps.geometry.spherical
        .computeLength((shapeObj.overlay as Polyline).getPath().getArray()) * this.FEET_IN_METER;
    }

    if (eventName === 'click') {
      setTimeout(() => {
        this.openInfoWindow(this.shapeMarker, 'shapeMarker');
      }, 300)
    }

    this.shapeIsOpen = true;
    this.cdr.detectChanges();
  }

  private computeAreaAndRadius(shape: OverlayCompleteEvent): { radius: number, area: number } {
    this.shapeType = shape.type;
    if (shape.type === OverlayType.CIRCLE) {
      const radius: number = (shape.overlay as Circle).getRadius() * this.MILES_IN_METER;
      //RP-7006
      //radius above is a percentage which is why we multiply by 2. This gets us an approximate area
      //which is good enough. Otherwise we are multiplyig something like .5 * .5 which becomes .25 when we actually need 1.
      return { radius, area: (radius * radius) * Math.PI };
    } else if (shape.type === OverlayType.POLYGON) {
      const area: number =
        google.maps.geometry.spherical.computeArea((shape.overlay as Polygon).getPath()) * this.SQUARE_MILES_IN_SQUARE_METER;
      return { radius: 0, area };
    } else if (shape.type === OverlayType.RECTANGLE) {
      const bounds: LatLngBounds = (shape.overlay as Rectangle).getBounds();
      const northEast: LatLng = bounds.getNorthEast();
      const southWest: LatLng = bounds.getSouthWest();
      const southEast: LatLng = new google.maps.LatLng(southWest.lat(), northEast.lng());
      const northWest: LatLng = new google.maps.LatLng(northEast.lat(), southWest.lng());
      const area: number =
        google.maps.geometry.spherical.computeArea([northEast, northWest, southWest, southEast]) * this.SQUARE_MILES_IN_SQUARE_METER;
      return { radius: 0, area };
    }
  }

  private getPointToDisplayInfoWindow(shapeObj: OverlayCompleteEvent): LatLngLiteral {
    if (shapeObj.type === OverlayType.POLYGON || shapeObj.type === OverlayType.POLYLINE) {
      const vertexArray: Array<LatLng> = (shapeObj.overlay as Polygon).getPath().getArray();
      let rightmostVertex: LatLngLiteral = { lat: vertexArray[0].lat(), lng: vertexArray[0].lng() };
      vertexArray.forEach((vertex: LatLng) => {
        if (vertex.lng() > rightmostVertex.lng) {
          rightmostVertex = { lat: vertex.lat(), lng: vertex.lng() };
        }
      });
      return rightmostVertex;
    } else {
      const bounds: LatLngBounds = (shapeObj.overlay as Circle).getBounds();
      const northEast: LatLng = bounds.getNorthEast();
      const southWest: LatLng = bounds.getSouthWest();
      return { lat: (northEast.lat() + southWest.lat()) / 2, lng: northEast.lng() };
    }
  }

  public onNameChange(value: string): void {
    const shapeToRename = this.shapeOverlays.find((shape: OverlayCompleteEvent) => shape.overlay.get('myID') === this.shapeId);
    if (shapeToRename) {
      shapeToRename.overlay.set('name', value);
      this.shapeName = value;
      this.store$.dispatch(new ChangeShape({ id: shapeToRename.overlay.get('myID'), name: value }));
    }
  }

  public deleteShape(): void {
    this.shapeIsOpen = false;

    const shape = this.shapeDistance
      ? this.findShape(this.measurementToolOverlays, this.shapeId)
      : this.findShape(this.shapeOverlays, this.shapeId);

    (shape && shape.type === OverlayType.POLYLINE)
      ? this.logMapDrawingMixPanel('Search - Measurement Removed', 'measurement')
      : this.logMapDrawingMixPanel('Search - Drawing Removed', shape.type);

    const action = (shape && shape.type === OverlayType.POLYLINE)
      ? new RemoveDrawingLine(this.shapeId)
      : new RemoveDrawingFigure(this.shapeId);

    this.store$.dispatch(action);
    this.cdr.detectChanges();
  }

  private findShape(shapes: Array<OverlayCompleteEvent>, shapeId: number): OverlayCompleteEvent {
    return shapes.find((shape: OverlayCompleteEvent) => shape.overlay.get('myID') === shapeId);
  }

  private clearShape(shape: OverlayCompleteEvent): void {
    google.maps.event.clearInstanceListeners(shape.overlay);
    shape.overlay.setMap(null);
  }

  public openShape(event: any) {
    this.shapeIsOpen = event;
    this.cdr.detectChanges();
  }

  public deleteAnnotation(a: GmMarker): void {
    this.store$.dispatch(new DeleteAnnotation(a.parcelId));
    this.logMapLocateAnnotationMixPanel('Search - Annotation Removed');
  }

  private getWmsOverlay(
    layers$: Observable<Array<LayerInfo>>,
    opacityLevel: number = 1,
    premiumUrl?: Observable<any>
  ): Observable<googleModel.maps.ImageMapType> {
    return layers$.pipe(
      debounceTime(ONE_SECOND),
      withLatestFrom(premiumUrl ? premiumUrl : of(null)),
      map((layers: [Array<LayerInfo>, string | null]) => {
        const enabledLayers =
          layers[0].filter((layer: LayerInfo) => !layer.needZoomOut && !layer.needZoomIn);
        return enabledLayers.length ? createBoundariesWmsOverlay(enabledLayers, opacityLevel, layers[1]) : null;
      }),
      untilDestroyed(this)
    );
  }

  private drawWmsOverlay(layers$: Observable<Array<LayerInfo>>, index: number): void {
    this.getWmsOverlay(layers$)
      .subscribe((wmsOverlay) => {
        this.map.overlayMapTypes.setAt(index, wmsOverlay);
      });
  }

  public showPictureViewer(parcelId: string, fipsCode: string, propertyData: PropertyData) {
    const payload: PropertyPhotosRequest = {
      parcelId,
      fipsCode,
      photoId: propertyData.MLS_PHOTO_ID,
      photoPath: propertyData.MLS_PHOTO_PATH,
      propertyData,
      listingNumber: propertyData.MLS_LISTING_NUMBER
    };
    this.store$.dispatch(new OpenPictureViewer(payload));
  }

  private onDriveDirectionResponse(response: DirectionsResult): void {
    if (this.driveDirectionIsOpen) {
      this.clearDirections();
    }
    const route: DirectionsRoute = response.routes[0];
    const bounds: LatLngBounds = route.bounds;
    const northEast: LatLng = bounds.getNorthEast();
    const southWest: LatLng = bounds.getSouthWest();

    this.driveDirectionLeg = route.legs[0];
    this.driveDirectionWindowLat = (northEast.lat() + southWest.lat()) / 2;
    this.driveDirectionWindowLong = northEast.lng();

    const routePolyline = new google.maps.Polyline(DRIVE_DIRECTION_ROUTE_STYLE);
    route.legs.forEach((leg: DirectionsLeg) => {
      leg.steps.forEach((step: DirectionsStep) => {
        step.path.forEach((segment: LatLng) => {
          routePolyline.getPath().push(segment);
        });
      });
    });

    const driveDirectionMarkerSize = 22;
    const originIcon: GmMarkerIcon = {
      url: 'assets/img/agm/origin.png',
      anchor: new google.maps.Point(driveDirectionMarkerSize / 2, driveDirectionMarkerSize / 2),
      scaledSize: new google.maps.Size(driveDirectionMarkerSize, driveDirectionMarkerSize)
    };
    const destinationIcon: GmMarkerIcon = {
      url: 'assets/img/agm/destination.png',
      anchor: new google.maps.Point(driveDirectionMarkerSize / 2, driveDirectionMarkerSize / 2),
      scaledSize: new google.maps.Size(driveDirectionMarkerSize, driveDirectionMarkerSize)
    };
    const originMarker = new google.maps.Marker({
      position: this.driveDirectionLeg.start_location,
      icon: originIcon,
      title: 'origin',
      map: this.map
    });
    const destinationMarker = new google.maps.Marker({
      position: this.driveDirectionLeg.end_location,
      icon: destinationIcon,
      title: 'destination',
      map: this.map
    });
    this.driveDirectionObjects.push(routePolyline, originMarker, destinationMarker);

    routePolyline.setMap(this.map);
    this.driveDirectionListeners.push(
      google.maps.event.addListener(originMarker, 'click', () => this.toggleDriveDirection()),
      google.maps.event.addListener(destinationMarker, 'click', () => this.toggleDriveDirection()),
      google.maps.event.addListener(routePolyline, 'click', () => this.toggleDriveDirection())
    );
    this.map.fitBounds(bounds, this.isMobile ? { top: -100, bottom: 520, right: 33 } as Padding : {
      top: 20,
      left: 40,
      right: 280
    } as Padding);
    this.driveDirectionIsOpen = true;
    this.driveDirectionWindowIsOpen = true;

    setTimeout(() => {
      this.openInfoWindow(this.driveMarker, 'driveMarker');
    }, 300)
  }

  public sendEmailDriveDirection(): void {
    this.store$.dispatch(new SendEmailDriveDirection({
      origin: this.driveDirectionLeg.start_address,
      destination: this.driveDirectionLeg.end_address
    }));
  }

  public driveDirectionClose(): void {
    this.driveDirectionWindowIsOpen = false;
    this.cdr.detectChanges();
  }

  public clearDirections(): void {

    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track('Search - Driving Directions Remove', {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });

    this.driveDirectionListeners.forEach((listener: MapsEventListener) => google.maps.event.removeListener(listener));
    this.driveDirectionObjects.forEach((obj: Marker | Polyline) => obj.setMap(null));
    this.driveDirectionListeners = [];
    this.driveDirectionObjects = [];
    this.driveDirectionClose();
    this.driveDirectionIsOpen = false;
    this.store$.dispatch(new SetDriveDirection({ from: '', to: '' }));
    this.store$.dispatch(new SetDriveDirectionResult(null));
  }

  private toggleDriveDirection(): void {
    if (this.isMobile) {
      return;
    }
    this.driveDirectionWindowIsOpen = !this.driveDirectionWindowIsOpen;

    setTimeout(() => {
      if (this.driveDirectionWindowIsOpen) {
        this.openInfoWindow(this.driveMarker, 'driveMarker');
      }
    }, 300)

    this.cdr.detectChanges();
  }

  public createIcon(url: string): GmMarkerIcon {
    return ({ url, scaledSize: { height: 20, width: 20 } as Size });
  }

  private locateOnMapHandler(): void {
    if (!this.map) {
      return;
    }

    if (this.autoCompleteManager) {
      return;
    }

    this.zone.run(() => {
      const htmlInput: HTMLInputElement = document.querySelector('#locate-on-map-js');
      this.autoCompleteManager = new google.maps.places.Autocomplete(htmlInput);
      this.autoCompleteManager.setComponentRestrictions({ 'country': ['us'] });
      this.autoCompleteManager.bindTo('bound', this.map);

      this.autoCompleteManager.addListener('place_changed', () => {
        const place = this.autoCompleteManager.getPlace();

        if (!place.geometry) {
          this.logMapLocateAnnotationMixPanel('Search - Location Remove');
          // User entered the name of a Place that was not suggested and
          // pressed the Enter key, or the Place Details request failed.
          this.ribbonService.openRibbon(RIBBON_TYPE.WARNING, `No details available for input: ${place.name}`);
          this.locateOnMapMarker = null;
          this.cdr.markForCheck();
          return;
        } else {
          this.logMapLocateAnnotationMixPanel('Search - Location Added');
        }

        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(this.LOCATE_ON_MAP_ZOOM_LVL);
        }

        this.locateOnMapMarker = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          draggable: false,
          title: place.name,
          icon: MapUtils.createIconForMarker('assets/img/agm/locate_on_map.png', { height: 20, width: 20 }),
        };

        this.cdr.markForCheck();
      });
    });
  }

  public openReports(propertyInfoMarker: GmMarker): void {
    const propertyData = propertyInfoMarker.propertyData;
    const propertyIdentifier = MapUtils.mapAgmMarkerToPropertyIdentifier(propertyInfoMarker);
    if (this.isMobile) {
      this.zone.run(() => this.navigationService.openSingleReport(
        {
          propertyData,
          propertyIdentifier
        }
      ));
    } else {
      this.zone.run(() => this.navigationService.openReports(
        {
          propertyData,
          propertyIdentifier
        },
        this.checkedProperties
      ));
    }

  }

  public openSingleReport(propertyInfoMarker: GmMarker): void {
    const propertyData = propertyInfoMarker.propertyData;
    const propertyIdentifier = MapUtils.mapAgmMarkerToPropertyIdentifier(propertyInfoMarker);
    this.zone.run(() => this.navigationService.openSingleReport({
      propertyData,
      propertyIdentifier
    })
    );
  }

  private getTotalShapesArea(): number {
    return this.shapeOverlays.reduce<number>(
      (acc: number, shape: OverlayCompleteEvent) =>
        acc + (shape.type !== OverlayType.POLYLINE ? this.computeAreaAndRadius(shape).area : 0), 0);
  }

  private showMessageIfTotalShapesAreaExceeded(totalShapesArea: number) {
    if (totalShapesArea > this.SHAPE_MAX_AREA) {
      debounce(
        () => this.ribbonService.openRibbon(
          RIBBON_TYPE.ERROR,
          `Total searchable area exceeded the limit of ${this.SHAPE_MAX_AREA} square miles. Please reduce the search area.`,
          { duration: this.RIBBON_SHOW_TIME }
        ),
        this.RIBBON_SHOW_TIME,
        true
      )();
    }
  }

  private adjustBounds(southwest: LatLngLiteral, northeast: LatLngLiteral): Array<LatLngLiteral> {
    const BOUNDS_SHRINK_PERCENTAGE = 0.1; // 10%
    function adjustmentAmount(value1: number, value2: number): number {
      return Math.abs(value1 - value2) * BOUNDS_SHRINK_PERCENTAGE;
    }

    const latAdjustment = adjustmentAmount(northeast.lat, southwest.lat);
    const lngAdjustment = adjustmentAmount(northeast.lng, southwest.lng);

    return [
      { lat: southwest.lat + latAdjustment, lng: southwest.lng + lngAdjustment },
      { lat: northeast.lat - latAdjustment, lng: northeast.lng - lngAdjustment }
    ];
  }

  public fetchPOI(categoryName: PointOfInterestProperty): Observable<Array<PointOfInterestPoints>> {
    return this.getPointOfInterestPoints(categoryName).pipe(
      map((result) => {
        const points = result.map((placeResult): PointOfInterestPoint => {
          return {
            geometry: {
              location: {
                lat: placeResult.geometry.location.toJSON().lat,
                lng: placeResult.geometry.location.toJSON().lng
              }
            },
            name: placeResult.name,
            icon: placeResult.icon,
            rating: placeResult.rating,
            vicinity: placeResult.vicinity
          };
        });
        const position = this.poiLayer.findIndex((e: PointOfInterestPoints) => Object.keys(e)[0] === categoryName);
        if (position >= 0) {
          this.poiLayer[position][categoryName] = [...points, ...this.poiLayer[position][categoryName]];
        } else {
          this.poiLayer.push({ [categoryName]: points });
        }
        this.cdr.detectChanges();
        return this.poiLayer;
      }),
      untilDestroyed(this)
    );
  }

  public getPointOfInterestPoints(point: PointOfInterestProperty): Observable<Array<PlaceResult>> {
    let poiPoints: Array<PlaceResult> = [];

    return new Observable<Array<PlaceResult>>((observer) => {
      // Each point may contain one or more Google Place types and we have to make a call for each one.
      const placesArray = PointOfInterestTypeToPlacesType[point] ? PointOfInterestTypeToPlacesType[point].split(',') : [];

      const finalResult = placesArray.reduce(async (
        previousPromise: Promise<Array<googleModel.maps.places.PlaceResult>>,
        place: string
      ) => {
        await previousPromise;
        return this.findNearby(place).then((result) => {
          if (result) {
            poiPoints = [...result, ...poiPoints];
          }
        });
      }, Promise.resolve());
      finalResult.then(() => {
        observer.next(poiPoints);
        observer.complete();
      });
    });
  }

  findNearby(place: string): Promise<Array<googleModel.maps.places.PlaceResult> | void> {
    return new Promise<Array<googleModel.maps.places.PlaceResult> | void>((resolve) => {
      new google.maps.places.PlacesService(this.map).nearbySearch({
        bounds: this.map.getBounds(),
        type: place
      }, (response: Array<googleModel.maps.places.PlaceResult>, status: googleModel.maps.places.PlacesServiceStatus) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          resolve(response);
        }
        resolve();
      });
    });
  }

  private logMapDrawingMixPanel(eventId: string, drawingType: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      shapeType: drawingType,
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });
  }

  private logMapLocateAnnotationMixPanel(eventId: string) {
    this.searchMixpanelUtil.setupMixPanelItems(this.sidePanelTabOpened);

    this.mixpanelService.track(eventId, {
      countyList: this.searchMixpanelUtil.countyList,
      searchType: this.sidePanelTabOpened,
      isSavedSearch: (this.sidePanelTabOpened === 'quickSearch') ? false :
        this.searchMixpanelUtil.selectedSearch !== 'My Search Default',
      searchParameters: this.searchMixpanelUtil.searchFieldNames.join('; '),
      numberOfResults: this.searchMixpanelUtil.totalSearchRecords
    });
  }

  public getPropertyInformation(propertyInfoMarker: GmMarker) {
    return { propertyIdentifier: MapUtils.mapAgmMarkerToPropertyIdentifier(propertyInfoMarker), propertyData: propertyInfoMarker.propertyData }
  }

  public loadParcelDisclaimer(latLong: LatLong): void {
    this.store$.dispatch(new OpenDisclaimer(latLong));
  }

  public closeInfoWindows(): void {
    this.infoWindows?.forEach((iw) => iw.close());
  }
}
